import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { API } from "@/App";
import axios from "axios";
import { 
  TrendingUp, 
  Award,
  Search,
  RefreshCw,
  ArrowRight,
  BarChart3,
  Target,
  Medal
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";

const QuartileBadge = ({ quartile }) => {
  const colors = {
    1: "bg-green-500/20 text-green-400 border-green-500/30",
    2: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    3: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    4: "bg-red-500/20 text-red-400 border-red-500/30"
  };
  
  const labels = {
    1: "Q1 - Top 25%",
    2: "Q2 - Top 50%",
    3: "Q3 - Top 75%",
    4: "Q4 - Bottom 25%"
  };
  
  return (
    <Badge className={`${colors[quartile]} border`}>
      {labels[quartile]}
    </Badge>
  );
};

export const ConsistentPerformersPage = () => {
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("Large Cap");
  const [selectedFund, setSelectedFund] = useState(null);
  const [rollingData, setRollingData] = useState(null);
  const [loadingRolling, setLoadingRolling] = useState(false);

  const categories = ["Large Cap", "Mid Cap", "Small Cap", "ELSS", "Hybrid", "Index"];

  const fetchQuartileData = async (category) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/funds/analytics/quartile-ranking?category=${encodeURIComponent(category)}&limit=20`);
      setFunds(response.data.funds || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRollingReturns = async (schemeCode, schemeName) => {
    setLoadingRolling(true);
    setSelectedFund({ schemeCode, schemeName });
    try {
      const response = await axios.get(`${API}/funds/analytics/rolling-returns/${schemeCode}?period_months=12`);
      setRollingData(response.data);
    } catch (error) {
      console.error("Error fetching rolling returns:", error);
    } finally {
      setLoadingRolling(false);
    }
  };

  useEffect(() => {
    fetchQuartileData(selectedCategory);
  }, [selectedCategory]);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="consistent-performers-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
            <Award className="w-8 h-8 text-primary" />
            Consistent Performers
          </h1>
          <p className="text-muted-foreground mt-1">
            Funds ranked by quartile with rolling returns analysis
          </p>
        </div>

        {/* Category Selection */}
        <div className="flex flex-wrap gap-2">
          {categories.map(cat => (
            <Button
              key={cat}
              variant={selectedCategory === cat ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory(cat)}
              data-testid={`category-${cat}`}
            >
              {cat}
            </Button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Quartile Rankings Table */}
          <Card className="bg-card/50 border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Medal className="w-5 h-5 text-primary" />
                Quartile Rankings - {selectedCategory}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="animate-pulse flex items-center gap-4">
                      <div className="w-8 h-8 bg-muted rounded-full" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-muted rounded w-3/4" />
                        <div className="h-3 bg-muted rounded w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {funds.map((fund, index) => (
                    <div
                      key={fund.schemeCode}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedFund?.schemeCode === fund.schemeCode
                          ? "border-primary bg-primary/10"
                          : "border-border/50 hover:border-primary/50 hover:bg-muted/30"
                      }`}
                      onClick={() => fetchRollingReturns(fund.schemeCode, fund.schemeName)}
                      data-testid={`fund-row-${fund.schemeCode}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold text-sm">
                            {fund.rank}
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-sm truncate max-w-[200px]">
                              {fund.schemeName}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              1Y: <span className={fund.returns1Y >= 0 ? 'text-green-500' : 'text-red-500'}>
                                {fund.returns1Y?.toFixed(2)}%
                              </span>
                            </div>
                          </div>
                        </div>
                        <QuartileBadge quartile={fund.quartile} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Rolling Returns Chart */}
          <Card className="bg-card/50 border-border/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                Rolling Returns Analysis
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingRolling ? (
                <div className="h-[400px] flex items-center justify-center">
                  <RefreshCw className="w-8 h-8 text-muted-foreground animate-spin" />
                </div>
              ) : rollingData ? (
                <div className="space-y-4">
                  <div className="text-sm font-medium truncate">{rollingData.scheme_name}</div>
                  
                  {/* Statistics */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="p-3 bg-muted/30 rounded-lg text-center">
                      <div className="text-lg font-bold text-primary">
                        {rollingData.statistics.consistency_score}%
                      </div>
                      <div className="text-xs text-muted-foreground">Consistency</div>
                    </div>
                    <div className="p-3 bg-muted/30 rounded-lg text-center">
                      <div className="text-lg font-bold text-green-500">
                        {rollingData.statistics.average_return}%
                      </div>
                      <div className="text-xs text-muted-foreground">Avg Return</div>
                    </div>
                    <div className="p-3 bg-muted/30 rounded-lg text-center">
                      <div className="text-lg font-bold text-green-400">
                        {rollingData.statistics.max_return}%
                      </div>
                      <div className="text-xs text-muted-foreground">Best Period</div>
                    </div>
                    <div className="p-3 bg-muted/30 rounded-lg text-center">
                      <div className="text-lg font-bold text-red-400">
                        {rollingData.statistics.min_return}%
                      </div>
                      <div className="text-xs text-muted-foreground">Worst Period</div>
                    </div>
                  </div>

                  {/* Chart */}
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={rollingData.rolling_returns}>
                        <defs>
                          <linearGradient id="colorReturn" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#F59E0B" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="end_date" stroke="#9CA3AF" tick={{ fontSize: 10 }} />
                        <YAxis stroke="#9CA3AF" tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1E293B', border: '1px solid #374151' }}
                          labelStyle={{ color: '#F8FAFC' }}
                          formatter={(value) => [`${value}%`, 'Return']}
                        />
                        <ReferenceLine y={0} stroke="#EF4444" strokeDasharray="3 3" />
                        <Area 
                          type="monotone" 
                          dataKey="return" 
                          stroke="#F59E0B" 
                          fillOpacity={1} 
                          fill="url(#colorReturn)" 
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>

                  <Link to={`/research/${rollingData.scheme_code}`}>
                    <Button variant="outline" size="sm" className="w-full">
                      View Fund Details <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="h-[400px] flex items-center justify-center text-center">
                  <div>
                    <Target className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">
                      Click on a fund to view its rolling returns analysis
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Info Card */}
        <Card className="bg-gradient-to-r from-primary/10 to-accent/10 border-primary/20">
          <CardContent className="p-6">
            <h3 className="font-semibold text-foreground mb-2">Understanding Quartile Rankings</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <QuartileBadge quartile={1} />
                <p className="text-muted-foreground mt-1">Best performers in category</p>
              </div>
              <div>
                <QuartileBadge quartile={2} />
                <p className="text-muted-foreground mt-1">Above average performers</p>
              </div>
              <div>
                <QuartileBadge quartile={3} />
                <p className="text-muted-foreground mt-1">Below average performers</p>
              </div>
              <div>
                <QuartileBadge quartile={4} />
                <p className="text-muted-foreground mt-1">Underperformers</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};
